import { z } from 'zod/v4';
import type { operations } from '@molchanovs/api-contract';

export type WarehouseInventoryResponse =
  operations['getApiV1WarehouseInventory']['responses'][200]['content']['application/json'];
export type WarehouseInventoryItem = WarehouseInventoryResponse['data']['items'][number];
export type WarehouseInventoryWarning = WarehouseInventoryItem['warnings'][number];
export type WarehouseOptionsResponse =
  operations['getApiV1WarehouseWarehouses']['responses'][200]['content']['application/json'];
export type WarehouseOption = WarehouseOptionsResponse['data']['items'][number];

type InventoryWireQuery = NonNullable<
  operations['getApiV1WarehouseInventory']['parameters']['query']
>;
export type WarehouseInventoryQuery = Omit<InventoryWireQuery, 'limit'> & { limit: number };

const sourceIdSchema = z
  .string()
  .regex(/^(0|[1-9][0-9]*)$/u)
  .max(100);
const warningSchema = z.enum([
  'null_quantity',
  'zero_quantity',
  'negative_quantity',
  'orphan_product',
  'missing_warehouse',
]);
const finiteQuantitySchema = z
  .string()
  .regex(/^-?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/u)
  .refine((value) => Number.isFinite(Number(value)));

/** Exact query validation before the web transport serializes parameters. */
export const warehouseInventoryQuerySchema = z
  .object({
    warehouseId: sourceIdSchema.optional(),
    q: z.string().trim().max(200).optional(),
    warning: warningSchema.optional(),
    limit: z.number().int().min(1).max(100),
    cursor: z.string().min(1).max(4096).optional(),
  })
  .strict();

const inventoryItemSchema = z.object({
  id: sourceIdSchema,
  sku: z.string().nullable(),
  productName: z.string().nullable(),
  warehouse: z.object({ id: sourceIdSchema.nullable(), name: z.string().nullable() }),
  currentQuantity: finiteQuantitySchema.nullable(),
  reservedQuantity: finiteQuantitySchema.nullable(),
  startingQuantity: finiteQuantitySchema.nullable(),
  availableQuantity: z.null(),
  availableQuantityConfidence: z.literal('unavailable'),
  unit: z.null(),
  warnings: z.array(warningSchema),
});

/** Known fields fail closed; unknown additive fields are stripped at every object level. */
export const warehouseInventoryResponseSchema: z.ZodType<WarehouseInventoryResponse> = z.object({
  data: z.object({ items: z.array(inventoryItemSchema).max(100) }),
  meta: z.object({
    limit: z.number().int().min(1).max(100),
    nextCursor: z.string().min(1).max(4096).nullable(),
    hasNextPage: z.boolean(),
    source: z.literal('postgresql-mirror'),
    origin: z.literal('bizagi-sqlserver'),
    generationId: z.string().nullable(),
    retrievedAt: z.iso.datetime(),
    snapshotStartedAt: z.iso.datetime().nullable(),
    snapshotCompletedAt: z.iso.datetime().nullable(),
    lastSuccessfulSyncAt: z.iso.datetime().nullable(),
    sourceUpdatedAt: z.iso.datetime().nullable(),
    syncIntervalMinutes: z.union([
      z.literal(60),
      z.literal(180),
      z.literal(360),
      z.literal(720),
      z.literal(1440),
    ]),
    syncStatus: z.enum(['idle', 'running', 'failed', 'blocked']),
    freshness: z.enum(['current', 'stale', 'never-synchronized']),
  }),
});

export const warehouseOptionsResponseSchema: z.ZodType<WarehouseOptionsResponse> = z.object({
  data: z.object({
    items: z.array(z.object({ id: sourceIdSchema, name: z.string().nullable() })).max(1000),
  }),
});
